import React, { useMemo, useState } from 'react';
import { Banknote, CalendarCheck, Search, SearchX, Users } from 'lucide-react';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/Select';
import { ExpertCard } from '../components/ExpertCard';
import { StatTile } from '../components/StatTile';
import { experts, expertiseFields } from '../data/experts';
import { averageRating, engagementCount, formatAED, totalPaid } from '../utils/expert';
import { tiers } from '../data/tiers';
import { getTier } from '../utils/tier';
type NationalityFilter = 'all' | 'emirati' | 'non-emirati';
type SortKey = 'rating' | 'engagements' | 'social' | 'cost-low' | 'cost-high';
export function ExpertsDirectory() {
  const [query, setQuery] = useState('');
  const [field, setField] = useState('all');
  const [nationality, setNationality] = useState<NationalityFilter>('all');
  const [sort, setSort] = useState<SortKey>('rating');
  const [tier, setTier] = useState('all');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = experts.filter(e => {
      if (nationality === 'emirati' && !e.isEmirati) return false;
      if (nationality === 'non-emirati' && e.isEmirati) return false;
      if (field !== 'all' && !e.expertise.some(x => x.name === field)) return false;
      if (tier !== 'all' && getTier(e).id !== tier) return false;
      if (!q) return true;
      return e.name.toLowerCase().includes(q) || e.title.toLowerCase().includes(q) || e.expertise.some(x => x.name.toLowerCase().includes(q));
    });
    return [...list].sort((a, b) => {
      switch (sort) {
        case 'engagements':
          return engagementCount(b) - engagementCount(a);
        case 'social':
          return b.social.overallScore - a.social.overallScore;
        case 'cost-low':
          return a.dailyRate - b.dailyRate;
        case 'cost-high':
          return b.dailyRate - a.dailyRate;
        default:
          return averageRating(b) - averageRating(a);
      }
    });
  }, [query, field, nationality, sort, tier]);
  const emiratiCount = experts.filter(e => e.isEmirati).length;
  const totalEngagements = experts.reduce((s, e) => s + engagementCount(e), 0);
  const paidAll = experts.reduce((s, e) => s + totalPaid(e), 0);
  const resetFilters = () => {
    setQuery('');
    setField('all');
    setNationality('all');
    setTier('all');
  };
  return <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-826" data-mp="20lvupo3umh77">
      <div className="flex items-center gap-4" data-id="element-827" data-mp="20np61gpepplj">
        <span className="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground" data-id="element-828" data-mp="2em6mctczfztf">
          <Users className="size-6" aria-hidden="true" data-id="element-829" data-mp="2ahyy0xafslvq" />
        </span>
        <div data-id="element-830" data-mp="3pziv9inln8mg">
          <h1 className="text-2xl font-semibold tracking-tight" data-id="element-831" data-mp="3pyzdq31v2m8y">Experts</h1>
          <p className="text-sm text-muted-foreground" data-id="element-832" data-mp="3lz8gphlqr9vz">
            Registered national and international experts available for BAK engagements.
          </p>
        </div>
      </div>

      <section aria-label="Directory summary" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-833" data-mp="2bnckdicwbn2">
        <StatTile label="Registered experts" value={String(experts.length)} hint="Across 8 core fields" icon={Users} data-id="element-834" data-mp="3jkabgynfl2m7" />
        <StatTile label="Emirati experts" value={`${emiratiCount}`} hint={`${Math.round(emiratiCount / experts.length * 100)}% of the network`} icon={Users} data-id="element-835" data-mp="3oibvblsol5yq" />
        <StatTile label="Completed engagements" value={String(totalEngagements)} hint="All-time" icon={CalendarCheck} data-id="element-836" data-mp="l1d5sw6h0pcv" />
        <StatTile label="Total paid to experts" value={formatAED(paidAll)} hint="Settled invoices" icon={Banknote} data-id="element-837" data-mp="6cz2yl9210wb" />
      </section>

      <section aria-label="Filters" className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center" data-id="element-838" data-mp="z262nnk8vwj5">
        <div className="relative flex-1" data-id="element-839" data-mp="13sw810so7zdu">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-840" data-mp="13txrntnr5u76" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, title or field…" className="pl-9" aria-label="Search experts" data-id="element-841" data-mp="3c7m5sua14t03" />
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-842" data-mp="2xdrev39txod6">
          <div className="inline-flex rounded-lg border bg-muted/50 p-0.5" role="group" aria-label="Nationality" data-id="element-843" data-mp="2xf2h2ynmmo26">
            {([['all', 'All'], ['emirati', 'Emirati'], ['non-emirati', 'Non-Emirati']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => setNationality(value)} aria-pressed={nationality === value} className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${nationality === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-844" data-mp="3l8kehpvexop0">
                {label}
              </button>)}
          </div>
          <Select value={field} onValueChange={setField} data-id="element-845" data-mp="p6w8ctwsy9m9">
            <SelectTrigger className="w-[210px]" aria-label="Field of expertise" data-id="element-846" data-mp="u3ebxqgec1x0">
              <SelectValue placeholder="Field of expertise" data-id="element-847" data-mp="z3iqhaxqumkl" />
            </SelectTrigger>
            <SelectContent data-id="element-848" data-mp="142vc5scvpnll">
              <SelectItem value="all" data-id="element-849" data-mp="141k7zrfiudty">All fields</SelectItem>
              {expertiseFields.map(f => <SelectItem key={f} value={f} data-id="element-850" data-mp="182dzsvw34cs4">{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tier} onValueChange={setTier} data-id="element-851" data-mp="1cwzzw2wpuiww">
            <SelectTrigger className="w-[160px]" aria-label="Tier" data-id="element-852" data-mp="1hsp62p38qy26">
              <SelectValue data-id="element-853" data-mp="1mt39tuxt7lue" />
            </SelectTrigger>
            <SelectContent data-id="element-854" data-mp="1muo7htff7kwh">
              <SelectItem value="all" data-id="element-855" data-mp="1rrxw56uzjsj9">All tiers</SelectItem>
              {tiers.map(t => <SelectItem key={t.id} value={t.id} data-id="element-856" data-mp="123seo3tae22u">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={v => setSort(v as SortKey)} data-id="element-857" data-mp="x5h7k0udm5ob">
            <SelectTrigger className="w-[180px]" aria-label="Sort by" data-id="element-858" data-mp="1btc0nicaqc1x">
              <SelectValue data-id="element-859" data-mp="1bvey2nfs37go" />
            </SelectTrigger>
            <SelectContent data-id="element-860" data-mp="171u73fclrdqz">
              <SelectItem value="rating" data-id="element-861" data-mp="171khvtzdtamc">Top rated</SelectItem>
              <SelectItem value="engagements" data-id="element-862" data-mp="1kt8s1rq2lp0o">Most engagements</SelectItem>
              <SelectItem value="social" data-id="element-863" data-mp="1fvfl2wgy0qzc">Social score</SelectItem>
              <SelectItem value="cost-low" data-id="element-864" data-mp="1fx9w9j2ex4q8">Cost: low to high</SelectItem>
              <SelectItem value="cost-high" data-id="element-865" data-mp="1ur4yi682bvsg">Cost: high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <p className="mt-4 text-xs text-muted-foreground" aria-live="polite" data-id="element-866" data-mp="ef9a1sn3are">
        Showing {filtered.length} of {experts.length} experts
      </p>

      {filtered.length > 0 ? <section aria-label="Experts" className="mt-3 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-id="element-867" data-mp="3rn2tgef58rxd">
          {filtered.map((expert, i) => <ExpertCard key={expert.id} expert={expert} index={i} data-id="element-868" data-mp="2c21vtqdx2te8" data-mpi={i} />)}
        </section> : <div className="mt-3 flex flex-col items-center rounded-xl border border-dashed py-16 text-center" data-id="element-869" data-mp="1xbuoo6td0wsu">
          <SearchX className="size-8 text-muted-foreground" aria-hidden="true" data-id="element-870" data-mp="22ao25z8ncnt0" />
          <p className="mt-3 text-sm font-medium" data-id="element-871" data-mp="1oby4ahe06k3l">No experts match these filters</p>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-872" data-mp="1sdjbbm3uw45v">Try a different field or clear your search.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={resetFilters} data-id="element-873" data-mp="1eejnggo63tar">Clear filters</Button>
        </div>}
    </main>;
}